import React, { Component, useEffect} from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MapPage from './MapPage';
import ReportPage from './ReportPage';
import Sidebar from './Sidebar';
import { buildLabelsAndColors } from "./utils/colorScale";
import { applyThemeCore } from "./utils/applyThemeCore";
import { isTrackingSchema } from "./utils/schemaTypes";
import { THEME_configs, THEME_OPTIONS } from "./config/themeConfigs";
import Header from "./Header";
import { CloseSharp } from '@mui/icons-material';
import UserMenu from './UserMenu';
import { DEMO_LABELS } from "./utils/demographicLabels";
import axios from 'axios';
import GuidedTour, { resetTour } from './GuidedTour';
import SelectionContext from './state/SelectionContext';




// FOR CM set to true to see the AI bot 
const ENABLE_CHAT = true
;

const SIDEBAR_WIDTH = 600;
// Points at the viz by workbook/view name rather than a /shared/<id> link.
// Shared IDs are per-publish: republishing the workbook mints a new one and the
// old link renders "something has gone wrong rendering this viz", which is how
// the previous N5JBKBWP6 link broke. The named path survives republishing.
// Share-link params (:sid, :redirect, :origin, :display_count, :language) are
// dropped — they're artifacts of the copy-link session, not embed settings.
const TABLEAU_URL =
  "https://public.tableau.com/views/ACC-Wave32026/Matrix?:embed=yes&:showVizHome=no&:toolbar=no&:device=browser";

const TableauOverlay = ({ isOpen, onClose, tableauUrl }) => {
  if (!isOpen) return null;

  // Constants for fixed Tableau size
  const TABLEAU_WIDTH = "1300px";
  const TABLEAU_HEIGHT = "700px";

  return (
    // Outer Overlay (Fixed Full Screen)
    <Box
      onClick={onClose} // Allows closing by clicking the background
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1500,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center', // ⬅️ Centers the content box horizontally
        alignItems: 'center',   // ⬅️ Centers the content box vertically
        transition: 'opacity 0.3s',
        overflow:"hidden"
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside the box
        sx={{
          position: "relative",
          width: TABLEAU_WIDTH,     
          height: TABLEAU_HEIGHT,    
          backgroundColor: "#fff",
          borderRadius: 3,
          boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1501,
            backgroundColor: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(0,0,0,0.1)',
            '&:hover': { backgroundColor: '#fff' },
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          }}
        >
          <CloseSharp />
        </IconButton>

        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <iframe
            src={tableauUrl}
            style={{
              width: "100%", 
              height: "100%",
              border: "none",
              display: "block",
            }}
            allowFullScreen
            title="Tableau Dashboard"
          />
        </Box>
      </Box>
    </Box>
  );
};


// THEME_configs and THEME_OPTIONS moved to ./config/themeConfigs (shared with mobile).
// NOTE (merge main→mobile): main's edits to these arrays (gamble/demo-gamble label
// "Gambling"→"Prediction Markets", demo-pol label "Political"→"Tracking", demo-gamble
// defaultVar "bh_gambling_last_year"→"bh_support_legal_sports_betting") were ported to
// ./config/themeConfigs.js (their new home) so main's intent is preserved.
const ALLOWED = new Set(THEME_OPTIONS.map(o => o.value));

function themeFromPath(pathname) {
  const seg = (pathname || '/').replace(/^\//, '').split('/')[0];
  if (seg === 'report') return 'report';
  return ALLOWED.has(seg) ? seg : 'pol';
}

export default class MainPage extends Component {
  static contextType = SelectionContext;

  constructor(props) {
    super(props);
    this.mapPageRef = React.createRef();
    const initialTheme = props.skipAuthRedirect
      ? (props.slug || 'gamble')
      : themeFromPath(window.location.pathname);
    const baseTheme = initialTheme === 'report' ? 'pol' : initialTheme;
    const themeEntry = THEME_configs.find(t => t.value === baseTheme);
  
    const { groupsById } = themeEntry.config();
    const theme_colours = themeEntry.colours();
    const { labels, colors } = buildLabelsAndColors(groupsById, theme_colours);
  
    const sectiontitle = THEME_OPTIONS.find(t => t.value === baseTheme).label;
  
    let pref_filter;
    let V_mode;
    const varType = groupsById[themeEntry.defaultVar].type;

    if (varType === "price_elast") {
      V_mode = "heat";
      pref_filter = groupsById[themeEntry.defaultVar].polarity.expect;
    } else if (varType === "likert") {
      V_mode = "heat";
      pref_filter = "net";
    } else {
      V_mode = "top"; 
    }
    
    const keys = Object.keys(groupsById).filter(key => key !== themeEntry.defaultVar);
    const randomKey = keys.length ? keys[Math.floor(Math.random() * keys.length)] : themeEntry.defaultVar;
    const defaultSchema = groupsById[themeEntry.defaultVar]?.schema;
    const defaultWaves = Array.isArray(groupsById[themeEntry.defaultVar]?.waves)
      ? [...groupsById[themeEntry.defaultVar].waves].sort((a, b) => b - a)
      : [];
    const latestWave = defaultWaves[0] ?? null;

    this.state = {
      isPanelOpen: true,
      selectedSection: initialTheme,
      sectionTitle: sectiontitle,
      blurbText: 'Pick a question, then compare across places or demographics.',
      Datablurb: '',
      selectedVariable: themeEntry.defaultVar,
      groupsById: groupsById,
      theme_colours,
      colorLabels: labels, 
      colorMap: colors,  
      viewMode: V_mode,
      preferenceFilter: pref_filter, 
      preferenceCategoryOptions: [],
      preferenceCategoryLabels: {},
      gender: null,     
      age: null,        
      ethnicity: null, 
      party: null, 
      education: null, 
      vote24:null,
      heatDomain: null,
      heatColor: null,
      topStrengthShading: false,
      selectedFeature: null,
      selectedFeatureData: { national: [], state: [], selected: [] },
      selectedStateName: null,
      user: { email: '', name: '', company: '' },
      open: false,
      ActionTargetVariables: {TargetVariable1: themeEntry.defaultVar, TargetVariable2:randomKey },
      ActionTargetSubVariables: {TargetSubVariable1: groupsById[themeEntry.defaultVar].keys[0], TargetSubVariable2:groupsById[randomKey].keys[0] },
      ActionTargetRanges: {TargetRange1: [0,1000], TargetRange2: [0,1000]},
      ActionTargetMAXRanges: {TargetMAXRange1: [0,1], TargetMAXRange2: [0,1]},
      ActionTargetConcentration: "all",
      ActionMode: "explore",
      audienceInsight: null,
      audienceInsightLoading: false,
      audienceInsightRequestId: 0,
      PossibleAreas:null,
      allowedSections: [],
      userReady: false,
      rankedItems: [],           
      mapLoading: false,
      compareWaves: false,
      compareKey: "__top__",
      waveOptions: isTrackingSchema(defaultSchema) ? defaultWaves : [],
      waveSelect: isTrackingSchema(defaultSchema) ? latestWave : null,
      autoUniformEnabled: true,
      selectedAreaIdToZoom: null, 
      hoverArea:null,
      poliInfo:null,
      geoLevel: "states",
      poliInfoToplevel:null,
      isTableauOpen: false, 
      isSidebarHover: false,
      demo:false,
      reportState: "",
      reportDistrict: "",
      chatHighlightAreas: [],
      chatStateCode: null,
      geoStateFilter: null,


    };
    this.toggleTableau = this.toggleTableau.bind(this);
  }

  // ── Highlights page interface ──────────────────────────────────────────────
  setHighlightsState = ({ variable, geoLevel, preferenceFilter, stateFilter = null, wave = null, gender = undefined, age = undefined, vote24 = undefined, ethnicity = undefined, education = undefined, party = undefined, heatDomain = undefined, heatColor = undefined, viewMode = undefined, topStrengthShading = undefined }) => {
    // heatDomain ([min,max]) pins the choropleth ramp to an absolute scale so
    // colour reads as strength of response rather than rank within the metric.
    if (heatDomain !== undefined) this.setState({ heatDomain });
    // heatColor overrides the single-hue ramp's base colour (scroll stories use
    // it to key the ramp to the active lens, e.g. blue/red electorates).
    if (heatColor !== undefined) this.setState({ heatColor });
    // topStrengthShading opts a categorical "top" map into shading by winning
    // margin instead of the app-wide flat colour-per-category default (see
    // restyle.js). Callers that don't pass it leave existing behavior untouched.
    if (topStrengthShading !== undefined) this.setState({ topStrengthShading });
    // viewMode is normally INFERRED from preferenceFilter, but two other paths
    // force it to "top" for categorical groups (handlegeoLevelChange, and
    // Sidebar's "categorical with no preferenceFilter" effect). A scroll story
    // asking for a continuous ramp on a categorical group needs to say so
    // explicitly, or it races those and loses.
    const forcedViewMode = viewMode;
    const group = this.state.groupsById[variable];
    // ethnicity/education/party are already supported end-to-end (MapPage props,
    // DEMOGRAPHIC_FILTERS in views.py, buildDemoPredicate); they just weren't
    // forwarded from the highlights entry point. Scroll stories need them to
    // feature demographic cuts on the map.
    const demoSlice = {
      ...(gender !== undefined ? { gender } : {}),
      ...(age !== undefined ? { age } : {}),
      ...(vote24 !== undefined ? { vote24 } : {}),
      ...(ethnicity !== undefined ? { ethnicity } : {}),
      ...(education !== undefined ? { education } : {}),
      ...(party !== undefined ? { party } : {}),
    };
    if (variable && group) {
      const pf = preferenceFilter || (group.type === "likert" ? "net" : "top");
      const isTracking = isTrackingSchema(group.schema);
      const waveOpts = isTracking
        ? [...(group.waves || [])].sort((a, b) => b - a)
        : [];
      const waveVal = wave ?? (waveOpts[0] ?? null);
      this.setState({
        selectedVariable: variable,
        preferenceFilter: pf,
        viewMode: forcedViewMode || (["top","second","third"].includes(pf) ? "top" : "heat"),
        autoUniformEnabled: true,
        geoStateFilter: stateFilter,
        ...(isTracking ? { waveOptions: waveOpts, waveSelect: waveVal } : {}),
        ...demoSlice,
      });
    } else if (preferenceFilter) {
      this.setState({
        preferenceFilter,
        viewMode: forcedViewMode || (["top","second","third"].includes(preferenceFilter) ? "top" : "heat"),
        geoStateFilter: stateFilter,
        ...(wave !== null ? { waveSelect: wave } : {}),
        ...demoSlice,
      });
    } else {
      this.setState({
        geoStateFilter: stateFilter,
        ...(forcedViewMode ? { viewMode: forcedViewMode } : {}),
        ...(wave !== null ? { waveSelect: wave } : {}),
        ...demoSlice,
      });
    }
    if (geoLevel && geoLevel !== this.state.geoLevel) {
      if (preferenceFilter) {
        this.setState({ geoLevel, autoUniformEnabled: true });
      } else {
        this.handlegeoLevelChange(geoLevel);
      }
    }
  };

  flyTo = ({ type, bounds, center, zoom, duration = 1200, padding }) => {
    const map = this._mapPageInstance?.mapRef?.current?.getMap?.();
    if (!map) return;
    // Ease-out cubic: fast start, gentle landing — smooths the between-slide
    // camera moves in the highlights reels (default easing feels abrupt).
    const easing = (t) => 1 - Math.pow(1 - t, 3);
    if (type === "fitBounds" || bounds) {
      map.fitBounds(bounds, { padding: padding ?? 40, duration, easing, essential: true });
    } else if (center) {
      map.flyTo({ center, zoom: zoom || 6, duration, easing, essential: true });
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  handleRankedUpdate = (items) => this.setState({ rankedItems: items });
  handleMapLoadingChange = (loading) => this.setState({ mapLoading: loading });
  handleAreaSelect   = (areaId) => this.setState({ selectedAreaIdToZoom: areaId });
  handleCompareWavesChange = (val) => this.setState({ compareWaves: val, ...(val ? { compareKey: "__top__" } : {}) });
  handleCompareKeyChange = (val) => this.setState({ compareKey: val });
  componentDidMount() {
    // Highlights embed mode: skip auth check, load map immediately
    if (this.props.skipAuthRedirect) {
      const theme = this.props.slug || 'gamble';
      this.applyTheme(theme);
      this.setState({ userReady: true, allowedSections: [theme] });
      return;
    }

    this._onPopState = () => {
      const selectedSection = themeFromPath(window.location.pathname);
      if (selectedSection === 'report') {
        this.setState({ selectedSection: 'report' });
        return;
      }
      this.applyTheme(selectedSection);
    };
    window.addEventListener("popstate", this._onPopState);

    axios
      .get("/api/user-info/")
      .then((response) => {
        const user = response.data || {};
        const email = user.email || "";
        const domain = email.split("@")[1]?.toLowerCase() || "";
        const isDemoUser = user.is_demo_user === true;
  
        if (!user || Object.keys(user).length === 0 || !email) {
          window.location.href = "/";
          return;
        }
  
        // Access control by company slug (with email domain fallback)
        const companySlug = user.company_slug || null;

        const clientAccess = {
          default: [],
          stack: ["pol", "healthcare","stateleg","ai","gamble", "example_client","selfdrive"],
          hanbury: ["pol","healthcare", "ai", "stateleg","gamble"],
          phrma: ["phrma"],
          acc: ["acc_politics", "acc_policy", "acc_climate"],
          pmi: ["pmi", "pol", "stateleg", "healthcare", "environ", "consumer"],
          demo:["demo-pol","demo-ai", "demo-health"],
          intuit:["pol","intuit"],
          nji:["pol","nji"],
          avia:["pol","selfdrive"],
          invariant:["pol","invariant_industry", "invariant_message", "invariant_h2h"]
        };

        // Resolve client identity from slug first, then fall back to email domain
        let resolvedClient;
        if (isDemoUser || companySlug === "demo") {
          resolvedClient = "demo";
        } else if (companySlug) {
          // Direct slug match
          const slugToClient = {
            "stack-data-strategy": "stack",
            "hanbury": "hanbury",
            "phrma": "phrma",
            "acc": "acc",
            "pmi": "pmi",
            "intuit": "intuit",
            "invariant": "invariant",
            "nji": "nji",
            "avia": "avia",
          };
          resolvedClient = slugToClient[companySlug] || (
            // fallback to domain if slug not in map
            domain.includes("stackdatastrategy.com") ? "stack"
            : domain.includes("hanburystrategy.com") ? "hanbury"
            : domain.includes("phrma.org") ? "phrma"
            : domain.includes("accaction.eco") ? "acc"
            : domain.includes("pmi.org") ? "pmi"
            : domain.includes("leavittdc.com") ? "intuit"
            : domain.includes("weareinvariant.com") ? "invariant"
            : domain.includes("personalcarecouncil.org") ? "invariant"
            : domain.includes("nji.org") ? "nji"
            : "default"
          );
        } else {
          // No slug — pure domain fallback
          resolvedClient = domain.includes("stackdatastrategy.demo") ? "demo"
            : domain.includes("phrma.org") ? "phrma"
            : domain.includes("pmi.org") ? "pmi"
            : domain.includes("hanburystrategy.com") ? "hanbury"
            : domain.includes("accaction.eco") ? "acc"
            : domain.includes("stackdatastrategy.com") ? "stack"
            : domain.includes("leavittdc.com") ? "intuit"
            : domain.includes("weareinvariant.com") ? "invariant"
            : domain.includes("personalcarecouncil.org") ? "invariant"
            : domain.includes("nji.org") ? "nji"
            : "default";
        }

        // Prefer allowed_tabs from the API (set on the Company model).
        // Fall back to the hardcoded clientAccess map for backwards compatibility.
        let allowedSections;
        if (user.allowed_tabs && user.allowed_tabs.length > 0) {
          allowedSections = [...user.allowed_tabs];
        } else {
          allowedSections = [...clientAccess.default];
          allowedSections.push(...(clientAccess[resolvedClient] || []));
        }

        if (resolvedClient === "demo") {
          this.setState({demo:true})
        }

        let initialTheme = themeFromPath(window.location.pathname);
        if (initialTheme !== "report" && !allowedSections.includes(initialTheme)) {
          initialTheme = allowedSections[0] || "pol";
          this.setThemeInUrl(initialTheme);
        }

        this.setState(
          { user, allowedSections, userReady: true,
            clientAccess: resolvedClient,
          },
          () => {
            if (initialTheme === "report") {
              this.setState({ selectedSection: "report" });
              return;
            }
            this.applyTheme(initialTheme);
          }
        );
      })
      .catch((error) => {
        console.error("Error fetching user info:", error);
        window.location.href = "/";
      });
  }
  

  
  componentWillUnmount() {
    window.removeEventListener('popstate', this._onPopState);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.selectedSection  !== this.state.selectedSection)  this.context.setSelectedSection(this.state.selectedSection);
    if (prevState.selectedVariable !== this.state.selectedVariable) this.context.setSelectedVariable(this.state.selectedVariable);
    if (prevState.preferenceFilter !== this.state.preferenceFilter) this.context.setPreferenceFilter(this.state.preferenceFilter);
    if (prevState.geoLevel         !== this.state.geoLevel)         this.context.setGeoLevel(this.state.geoLevel);
    if (prevState.waveSelect       !== this.state.waveSelect)       this.context.setWaveSelect(this.state.waveSelect);
    if (prevState.compareWaves     !== this.state.compareWaves)     this.context.setCompareWaves(this.state.compareWaves);
    if (prevState.compareKey       !== this.state.compareKey)       this.context.setCompareKey(this.state.compareKey);
  }

  setThemeInUrl = (theme) => {
    if (theme === 'report') {
      const newUrl = '/report';
      if (window.location.pathname !== newUrl) {
        window.history.pushState({}, '', newUrl);
      }
      this.setState({ selectedSection: 'report' });
      return;
    }
    if (!ALLOWED.has(theme)) return;
    const newUrl = '/' + theme;
    if (window.location.pathname !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
    this.applyTheme(theme);
  };

  applyTheme = (theme) => {
    // Pure, deterministic state payload — single source of truth in applyThemeCore.
    const core = applyThemeCore({
      theme,
      currentVariable: this.state.selectedVariable,
      themeConfigs: THEME_configs,
      themeOptions: THEME_OPTIONS,
      buildLabelsAndColors,
    });
    const { defaultVar, ...statePayload } = core; // defaultVar is non-state

    /// totally unnecessary over the top, randomly chooses a variable which is NOT the default variable for use as the second variable in the targetting action mode
    /// nondeterministic — stays in the wrapper, never part of the setState payload
    const keys = Object.keys(core.groupsById).filter(key => key !== defaultVar);
    const randomKey = keys[Math.floor(Math.random() * keys.length)];

    this.setState(statePayload, () => {
      this.handleVariableChange(this.state.selectedVariable);
      this.handleActionTargetVariableChange({val:defaultVar,varId:"1"});
      this.handleActionTargetVariableChange({val:randomKey,varId:"2"});
    });
  };

  handleFeatureSelect = ({ feature, data, stateCode, stateName, selectedStateName, selectedAreaLabel, selectedFeatureLevel }) => {
    console.log("[sidebar] update", {
      wave: this.state.selectedWave,
      selectedFeature: feature?.name || selectedAreaLabel || null,
      nationalCount: data?.national?.length || 0,
      stateCount: data?.state?.length || 0,
      selectedCount: data?.selected?.length || 0,
    });
    this.setState({
      selectedFeature: feature || (selectedAreaLabel ? { name: selectedAreaLabel, level: selectedFeatureLevel || null } : null),
      selectedFeatureLevel: feature?.level || selectedFeatureLevel || null,
      selectedFeatureData: data || { national: [], state: [], selected: [] },
      selectedStateName: selectedStateName || stateName || null,
    });
  };
  
  toggleTableau = () => {
    this.setState(prevState => ({
      isTableauOpen: !prevState.isTableauOpen,
    }));
  }

  togglePanel = () => {
    this.setState(
      s => ({ isPanelOpen: !s.isPanelOpen }),
      () => {
        let elapsed = 0;
        const interval = setInterval(() => {
          window.dispatchEvent(new Event('resize'));
          elapsed += 50;
          if (elapsed >= 350) clearInterval(interval);
        }, 50);
      }
    );
  };

  

  handleVariableChange = (val) => {
    const themeEntry = THEME_configs.find(t => t.value === this.state.selectedSection);
    // console.log("[Sidebar] selectedVariable ->", val);
    const selectedVariable = val || themeEntry.defaultVar;
    const variableType = this.state.groupsById[selectedVariable].type ;
    const variableSchema = this.state.groupsById[selectedVariable].schema;
    const waves = Array.isArray(this.state.groupsById[selectedVariable]?.waves)
      ? [...this.state.groupsById[selectedVariable].waves].sort((a, b) => b - a)
      : [];
    const latestWave = waves[0] ?? null;

    

      let preferenceFilter;
      if (variableType === "price_elast") {
          preferenceFilter = this.state.groupsById[selectedVariable].polarity.expect;
        } else  if (variableType === "likert"){
          preferenceFilter = "net";
        } else {
          preferenceFilter = "top";

        }

      this.setState({
        selectedVariable,
        preferenceFilter,
        viewMode: ["top", "second", "third"].includes(preferenceFilter) ? "top" : "heat",
        waveOptions: isTrackingSchema(variableSchema) ? waves : [],
        waveSelect: isTrackingSchema(variableSchema) ? latestWave : null,
        compareWaves: false,
        compareKey: "__top__",
        autoUniformEnabled: true,

      
      });

      if(val==="pol_midterm_vote_choice"){
        this.handlegeoLevelChange("cd")
      }

    
  };

  handleActionTargetVariableChange = ({val,varId}) => {
    const themeEntry = THEME_configs.find(t => t.value === this.state.selectedSection);
    const selectedVariable = val || themeEntry.defaultVar;
    const variableType = this?.state?.groupsById[selectedVariable]?.type ;
    const variable = this?.state?.groupsById[selectedVariable];
    const variableSchema = variable?.schema;
    const waves = Array.isArray(variable?.waves) ? [...variable.waves].sort((a, b) => b - a) : [];
    const latestWave = waves[0] ?? null;
    
      let preferenceFilter;
      switch(variableType){
      case "price_elast":
        preferenceFilter = variable?.polarity?.expect || "net"
        break
      default:
        preferenceFilter = (variable?.keys && variable.keys[0]) ? variable.keys[0] : "net"
        break
      }


        this.setState((prevState) => {
          const nextSubVar1 =
            varId === "1" ? preferenceFilter : prevState.ActionTargetSubVariables.TargetSubVariable1;
          const nextSubVar2 =
            varId === "2" ? preferenceFilter : prevState.ActionTargetSubVariables.TargetSubVariable2;

            
          return {
            ActionTargetVariables: {
              TargetVariable1:
                varId === "1" ? selectedVariable : prevState.ActionTargetVariables.TargetVariable1,
              TargetVariable2:
                varId === "2" ? selectedVariable : prevState.ActionTargetVariables.TargetVariable2,
            },
            ActionTargetSubVariables: {
              TargetSubVariable1: nextSubVar1,
              TargetSubVariable2: nextSubVar2,
            },
            ActionTargetRanges: {
              TargetRange1: nextSubVar1 === "net" ? [-1, 1] : [0, 1000],
              TargetRange2: nextSubVar2 === "net" ? [-1, 1] : [0, 1000],
            },
            ...(varId === "1"
              ? {
                  waveOptions: isTrackingSchema(variableSchema) ? waves : [],
                  waveSelect: isTrackingSchema(variableSchema) ? latestWave : null,
                  compareWaves: false,
                  compareKey: "__top__",
                }
              : {}),
          };
        });


    
  };

  handleActionTargetSubVariableChange = ({val,varId}) => {

        
        this.setState((prevState) => {
          const nextSubVar1 =
            varId === "1" ? val : prevState.ActionTargetSubVariables.TargetSubVariable1;
          const nextSubVar2 =
            varId === "2" ? val : prevState.ActionTargetSubVariables.TargetSubVariable2;

          return {
              ActionTargetSubVariables: {
                TargetSubVariable1:
                  varId === "1"
                    ? val
                    : this.state.ActionTargetSubVariables.TargetSubVariable1,
                TargetSubVariable2:
                  varId === "2"
                    ? val
                    : this.state.ActionTargetSubVariables.TargetSubVariable2,
              },
            ActionTargetRanges: {
              TargetRange1: nextSubVar1 === "net" ? [-1000, 1000] : [0, 1000],
              TargetRange2: nextSubVar2 === "net" ? [-1000, 1000] : [0, 1000],
            },
          }
      });

    
  };
  handleActionTargetRangesChange = ({ val, varId }) => {
    this.setState((prevState) => ({
      ActionTargetRanges: {
        TargetRange1:
          varId === "1" ? val : prevState.ActionTargetRanges.TargetRange1,
        TargetRange2:
          varId === "2" ? val : prevState.ActionTargetRanges.TargetRange2,
      },
    }));
  };
  handleActionTargetConcentrationChange = (val) =>
    this.setState({ ActionTargetConcentration: val });

handleActionTargetMAXRangesChange = ({ val, varId }) => {
  // fallback if val is missing or not an array
  const safeVal = Array.isArray(val)
    ? val.map((n, i) => (isNaN(n) ? (i === 0 ? 0 : 1) : n)) // replace NaN: min→0, max→1
    : [0, 1];

  this.setState((prevState) => ({
    ActionTargetMAXRanges: {
      TargetMAXRange1:
        varId === "1"
          ? safeVal
          : prevState.ActionTargetMAXRanges?.TargetMAXRange1 ?? [0, 1],
      TargetMAXRange2:
        varId === "2"
          ? safeVal
          : prevState.ActionTargetMAXRanges?.TargetMAXRange2 ?? [0, 1],
    },
  }));
};
  handleGenderChange     = (val) => this.setState({ gender: val });
  handleAgeChange        = (val) => this.setState({ age: val });
  handleEthnicityChange  = (val) => this.setState({ ethnicity: val });
  handlePartyChange      = (val) => this.setState({ party: val });
  handleEducationChange  = (val) => this.setState({ education: val });
  handleVote24Change  = (val) => this.setState({ vote24: val });
  handleActionModeChange = (val) => {
    if (val === "filter") {
      const { selectedVariable, groupsById, preferenceFilter } = this.state;
      const group = groupsById?.[selectedVariable];
      if (selectedVariable && group) {
        const nextSubVar =
          preferenceFilter && group.keys?.includes(preferenceFilter)
            ? preferenceFilter
            : (group.keys?.[0] ?? "net");

        this.setState((prevState) => ({
          ActionMode: val,
          ActionTargetVariables: {
            ...prevState.ActionTargetVariables,
            TargetVariable1: selectedVariable,
          },
          ActionTargetSubVariables: {
            ...prevState.ActionTargetSubVariables,
            TargetSubVariable1: nextSubVar,
          },
          ActionTargetRanges: {
            ...prevState.ActionTargetRanges,
            TargetRange1: nextSubVar === "net" ? [-1, 1] : [0, 1000],
          },
        }));
        return;
      }
    }

    if (val === "explore") {
      const { ActionTargetVariables, ActionTargetSubVariables, groupsById } = this.state;
      const nextVariable = ActionTargetVariables?.TargetVariable1;
      const group = groupsById?.[nextVariable];
      if (nextVariable && group) {
        const nextPreference =
          ActionTargetSubVariables?.TargetSubVariable1 &&
          (ActionTargetSubVariables.TargetSubVariable1 === "net" ||
            group.keys?.includes(ActionTargetSubVariables.TargetSubVariable1))
            ? ActionTargetSubVariables.TargetSubVariable1
            : (group.type === "categorical" ? "top" : (group.keys?.[0] ?? "net"));

        const nextViewMode =
          nextPreference === "top" || nextPreference === "second" || nextPreference === "third"
            ? "top"
            : "heat";

        this.setState({
          ActionMode: val,
          selectedVariable: nextVariable,
          preferenceFilter: nextPreference,
          viewMode: nextViewMode,
          autoUniformEnabled: true,
        });
        return;
      }
    }

    this.setState({ ActionMode: val });
  };
  handleReportStateChange = (val) => this.setState({ reportState: val, reportDistrict: "" });
  handleReportDistrictChange = (val) => this.setState({ reportDistrict: val });
  handlePossibleAreasChange     = (val) => this.setState({ PossibleAreas: val });
  handleSidebarHoverChange = (val) => this.setState({ isSidebarHover: val });
  handleAudienceInsightRequest = () =>
    this.setState((prev) => ({ audienceInsightRequestId: prev.audienceInsightRequestId + 1 }));
  clearAllDemos = () => this.setState({
    gender: null, age: null, ethnicity: null, party: null, education: null,vote24:null
  });
  handlePreferenceFilterChange = (val) => {
    this.setState((prev) => {
      const viewMode =
        val === "top" || val === "second" || val === "third"
          ? "top"
          : "heat";
  
      return { preferenceFilter: val, viewMode, autoUniformEnabled: false };
    });
  };
  
  
  handlehoverAreaChange     = (val) => this.setState({ hoverArea: val });
  handlepoliInfoChange     = (val) => this.setState({ poliInfo: val });
  handlegeoLevelChange     = (val) => {
    const group = this.state.groupsById?.[this.state.selectedVariable];
    const isCategorical = group?.type === "categorical";
    const isExplore = this.state.ActionMode === "explore";
    const next = { geoLevel: val, autoUniformEnabled: true };
    if (val !== "states" && isExplore && isCategorical) {
      next.preferenceFilter = "top";
      next.viewMode = "top";
    }
    this.setState(next);
  };
  handlepoliInfoToplevelChange     = (val) => this.setState({ poliInfoToplevel: val });
  handlewaveSelectChange     = (val) => this.setState({ waveSelect: val });
  handlewaveOptionsChange     = (val) => this.setState({ waveOptions: val });

  

  render() {
    const {
      isPanelOpen,
      sectionTitle,
      blurbText,
     // user, 
     // open,
      selectedVariable, 
      viewMode,
      preferenceFilter,
      selectedSection,
      groupsById,
      selectedAreaIdToZoom,
      rankedItems,
      //selectedFeatureData,

      // maps + filters
      theme_colours,

      gender, age, ethnicity, party, education,vote24
    } = this.state;
    const selectedDemographics = {
      gender: gender ? DEMO_LABELS.gender[gender] : null,
      age: age ? DEMO_LABELS.age[age] : null,
      ethnicity: ethnicity ? DEMO_LABELS.ethnicity[ethnicity] : null,
      party: party ? DEMO_LABELS.party[party] : null,
      education: education ? DEMO_LABELS.education[education] : null,
      vote24: vote24 ? DEMO_LABELS.vote24[vote24] : null,
    };
    if (!this.state.userReady) {
      return (
        <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            border: '4px solid rgba(0,0,0,0.12)',
            borderTopColor: '#2563eb',
            animation: 'spin 0.9s linear infinite',
          }} />
        </Box>
      );
    }
    const visibleThemes = THEME_OPTIONS.filter(opt =>
      this.state.allowedSections?.includes(opt.value)
    );
  //  console.log("CHECK", this.state.selectedFeatureData)
    // hideChrome (highlights-embedded map only) → 100% so the map inherits its
    // fixed 40dvh panel and the canvas matches the box; otherwise 100vh (the entire
    // normal app — desktop routes + /report — stays byte-identical).
    return (
      <Box sx={{ height: this.props.hideChrome ? '100%' : '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          })}
        >
         
         {!this.props.hideChrome && <Header
          visibleThemes={visibleThemes}
          selectedSection={this.state.selectedSection}
          clientAccess={this.state.clientAccess}
          setThemeInUrl={this.setThemeInUrl}
          UserMenu={UserMenu}
          user={this.state.user}
          onLogout={this.handleLogout}
          onTableauOpen={this.toggleTableau}
          showReportTab
          reportActive={this.state.selectedSection === "report"}
          onReportOpen={() => {
            this.setThemeInUrl("report");
          }}
          onTourRestart={() => {
            resetTour();
            this.setState(s => ({ tourForceRun: (s.tourForceRun || 0) + 1 }));
          }}
        />}


{this.state.selectedSection === 'report' ? (
  <Box sx={{ flexGrow: 1, height: "100%", overflowY: "auto" }}>
    <ReportPage
      showHeader={false}
      assignedClientAccess={this.state.clientAccess}
      user={this.state.user}
    />
  </Box>
) : (
       
          <Box sx={{ display: 'flex', flexGrow: 1, height: '100%', position: 'relative', width: '100%' }}>
            <Box
              sx={{
                flexGrow: 1,
                transition: 'all 0.35s ease',
                width: this.props.hideChrome ? '100%' : `calc(100% - ${isPanelOpen ? SIDEBAR_WIDTH : 0}px)`,
                backgroundColor: '#f8f9fa',
                position: 'relative',
                overflowY: 'auto',
              }}
            >
              <MapPage
                onInstance={(inst) => { this._mapPageInstance = inst; }}
                hideToolbar={!!this.props.hideChrome}
                disableGeoClick={!!this.props.hideChrome}
                disableInteraction={!!this.props.disableMapInteraction}
                geoStateFilter={this.state.geoStateFilter}
                selectedSection={selectedSection}
                selectedGroupId={selectedVariable}
                preferenceFilter={preferenceFilter}
                heatDomain={this.state.heatDomain}
                heatColor={this.state.heatColor}
                topStrengthShading={this.state.topStrengthShading}
                onPreferenceFilterChange={this.handlePreferenceFilterChange}
                autoUniformEnabled={this.state.autoUniformEnabled}
                onAutoUniformApplied={() => this.setState({ autoUniformEnabled: false })}
                groupsById={groupsById}                    
                genderFilter={gender}
                ageFilter={age}
                ethnicityFilter={ethnicity}
                partyFilter={party}
                educationFilter={education}
                selectedDemographics={selectedDemographics} 
                vote24Filter={vote24}
                mode={viewMode}
                setBlurb={(blurb) => this.setState({ Datablurb: blurb })}
                theme_colours={theme_colours}
                onFeatureSelect={this.handleFeatureSelect}

                ActionTargetVariables={this.state.ActionTargetVariables}
                ActionTargetSubVariables={this.state.ActionTargetSubVariables}
                ActionTargetRanges = {this.state.ActionTargetRanges}
              ActionTargetConcentration={this.state.ActionTargetConcentration}
              ActionMode={this.state.ActionMode}

              handleActionTargetMAXRangesChange={this.handleActionTargetMAXRangesChange}
              onAudienceInsightChange={(val) => this.setState({ audienceInsight: val })}
              onAudienceInsightLoadingChange={(val) => this.setState({ audienceInsightLoading: val })}
              audienceInsightRequestId={this.state.audienceInsightRequestId}
              sidebarHovering={this.state.sidebarHovering}

                handlePossibleAreasChange={this.handlePossibleAreasChange}
                PossibleAreas={this.state.PossibleAreas}

                onRankedUpdate={this.handleRankedUpdate}     
                selectedAreaIdToZoom={selectedAreaIdToZoom} 

                hoverArea={this.state.hoverArea}
                handlehoverAreaChange={this.handlehoverAreaChange}
                poliInfo={this.state.poliInfo}
                handlepoliInfoChange={this.handlepoliInfoChange}
                geoLevel={this.state.geoLevel}
                handlegeoLevelChange={this.handlegeoLevelChange}
                poliInfoToplevel={this.state.poliInfoToplevel}
                handlepoliInfoToplevelChange={this.handlepoliInfoToplevelChange}
                colorLabels={this.state.colorLabels}
                colorMap={this.state.colorMap}
                isSidebarHover={this.state.isSidebarHover}
                onClearDemographics={this.clearAllDemos}
                onLoadingChange={this.handleMapLoadingChange}

                waveSelect={this.state.waveSelect}
                handlewaveSelectChange={this.handlewaveSelectChange}

                waveOptions={this.state.waveOptions}
                handlewaveOptionsChange={this.handlewaveOptionsChange}
                compareWaves={this.state.compareWaves}
                onCompareWavesChange={this.handleCompareWavesChange}
                compareKey={this.state.compareKey}
                onCompareKeyChange={this.handleCompareKeyChange}
                demo={this.state.demo}
                chatHighlightAreas={this.state.chatHighlightAreas}
                onClearHighlights={() => this.setState({ chatHighlightAreas: [] })}
                chatStateCode={this.state.chatStateCode}
                geoPreloadOrder={this.props.geoPreloadOrder}

              />
            </Box>

            {!this.props.hideChrome && <>
            <Sidebar
              selectedFeatureData={this.state.selectedFeatureData}
              selectedAreaLabel={this.state.selectedFeature?.name}
              selectedStateName={this.state.selectedStateName}
              selectedFeature={this.state.selectedFeature}
              isPanelOpen={isPanelOpen}
              togglePanel={this.togglePanel}
              sectionTitle={sectionTitle}
              blurbText={blurbText}
              Datablurb = {this.state.Datablurb}
              groupsById={groupsById}  
              selectedVariable={selectedVariable}
              onVariableChange={this.handleVariableChange}
              preferenceFilter={preferenceFilter}
              preferenceCategoryOptions={[]}    // not used for POL
              preferenceCategoryLabels={{}}     // not used for POL
              onPreferenceFilterChange={this.handlePreferenceFilterChange}
              viewMode={viewMode}
              genderFilter={gender}      onGenderChange={this.handleGenderChange}
              ageFilter={age}            onAgeChange={this.handleAgeChange}
              ethnicityFilter={ethnicity}onEthnicityChange={this.handleEthnicityChange}
              partyFilter={party}        onPartyChange={this.handlePartyChange}
              educationFilter={education}onEducationChange={this.handleEducationChange}
              vote24Filter={vote24} onVote24Change={this.handleVote24Change}
              onClearAll={this.clearAllDemos}
              theme_colours={theme_colours}
              selectedFeatureLevel={this.state.selectedFeatureLevel} // 👈 new
              ActionTargetVariables={this.state.ActionTargetVariables}
              onActionTargetVariableChange = {this.handleActionTargetVariableChange}
              ActionTargetSubVariables={this.state.ActionTargetSubVariables}
              handleActionTargetSubVariableChange = {this.handleActionTargetSubVariableChange}
              ActionTargetRanges = {this.state.ActionTargetRanges}
              ActionTargetMAXRanges = {this.state.ActionTargetMAXRanges}
              handleActionTargetRangesChange={this.handleActionTargetRangesChange}
              ActionTargetConcentration={this.state.ActionTargetConcentration}
              handleActionTargetConcentrationChange={this.handleActionTargetConcentrationChange}
              audienceInsight={this.state.audienceInsight}
              audienceInsightLoading={this.state.audienceInsightLoading}
              onAudienceInsightRequest={this.handleAudienceInsightRequest}
              ActionMode={this.state.ActionMode}
              handleActionModeChange={this.handleActionModeChange}
              reportState={this.state.reportState}
              reportDistrict={this.state.reportDistrict}
              onReportStateChange={this.handleReportStateChange}
              onReportDistrictChange={this.handleReportDistrictChange}
              selectedDemographics={selectedDemographics} 
              rankedItems={rankedItems}                  
              onAreaSelect={(area) => this.handleAreaSelect(area.id)} 

                     hoverArea={this.state.hoverArea}
                handlehoverAreaChange={this.handlehoverAreaChange}
                poliInfo={this.state.poliInfo}
                handlepoliInfoChange={this.handlepoliInfoChange}
                geoLevel={this.state.geoLevel}
                handlegeoLevelChange={this.handlegeoLevelChange}
                poliInfoToplevel={this.state.poliInfoToplevel}
                handlepoliInfoToplevelChange={this.handlepoliInfoToplevelChange}
              colorLabels={this.state.colorLabels}
              colorMap={this.state.colorMap}
              selectedTheme={selectedSection}
              compareWaves={this.state.compareWaves}
              waveSelect={this.state.waveSelect}
              onSidebarHoverChange={this.handleSidebarHoverChange}
              mapLoading={this.state.mapLoading}
                demo={this.state.demo}
              showChatTab={ENABLE_CHAT && this.state.userReady && this.state.clientAccess === 'stack' && this.state.user?.is_staff === true}
              chatContext={{
                state: this.state.selectedStateName,
                district: this.state.selectedFeature?.name,
                theme: this.state.selectedSection,
                level: this.state.geoLevel,
              }}
              onHighlightAreas={(areas, queryUsed) => {
                this.setState({ chatHighlightAreas: areas });
                if (queryUsed?.theme && queryUsed.theme !== this.state.selectedSection) {
                  this.setThemeInUrl(queryUsed.theme);
                }
                if (queryUsed?.variable) {
                  setTimeout(() => this.handleVariableChange(queryUsed.variable), 50);
                }
                const targetLevel = queryUsed?.level || "cd";
                if (this.state.geoLevel !== targetLevel) {
                  this.handlegeoLevelChange(targetLevel);
                }
                if (queryUsed?.state_codes?.length === 1) {
                  this.setState({ chatStateCode: queryUsed.state_codes[0] });
                } else {
                  this.setState({ chatStateCode: null });
                }
              }}
              chatHighlightCount={(this.state.chatHighlightAreas || []).length}
              onClearHighlights={() => this.setState({ chatHighlightAreas: [] })}
            />

            <TableauOverlay
              isOpen={this.state.isTableauOpen}
              onClose={this.toggleTableau}
              tableauUrl={TABLEAU_URL}
            />
            </>}

          </Box>
)}
        </Box>
        <GuidedTour
          forceRun={this.state.tourForceRun || 0}
          onFinish={() => {}}
        />
      </Box>
      
    );
  }
}
